import writeXlsxFile, { Cell, Row, SheetData } from 'write-excel-file/browser';
import { Part, PartCD, SummaryData } from 'src/app/summary-data';

const HEADER_TEXT_COLOR = '#FFFFFF';
const HEADER_BACKGROUND_COLOR = '#00629D';
const COLUMN_COUNT = 6;

// ExcelJS applied wrapText to the whole "Name and Address" column.
const WRAPPED_COLUMN_INDEX = 3;

// The original code addressed amended Part D rows as `startRow + partdRow`, where `startRow` was 11.
const AMENDED_ROW_OFFSET = 11;

/** Writes rows by 1-based row number, mirroring how ExcelJS addressed the worksheet. */
interface SparseSheet {
    readonly maxRow: number;
    setRow(rowNumber: number, cells: Row): void;
    /** Appends after the last written row and returns that row number. */
    addRow(cells: Row): number;
    toSheetData(): SheetData;
}

function createSparseSheet(): SparseSheet {
    const rowsByNumber = new Map<number, Row>();
    let maxRow = 0;

    return {
        get maxRow() {
            return maxRow;
        },
        setRow(rowNumber: number, cells: Row) {
            rowsByNumber.set(rowNumber, cells);
            maxRow = Math.max(maxRow, rowNumber);
        },
        addRow(cells: Row) {
            const rowNumber = maxRow + 1;
            this.setRow(rowNumber, cells);
            return rowNumber;
        },
        toSheetData(): SheetData {
            const sheetData: SheetData = [];
            for (let rowNumber = 1; rowNumber <= maxRow; rowNumber++) {
                sheetData.push(rowsByNumber.get(rowNumber) ?? []);
            }
            return sheetData;
        }
    };
}

const COLUMNS = [
    { width: 25 },
    { width: 25 },
    { width: 25 },
    { width: 50 },
    { width: 25 },
    { width: 25 }
];

const TABLE_COLUMN_HEADINGS = [
    'Plan Status',
    'Enrollment Date',
    'Termination Date',
    'Name and Address',
    'Contract Number',
    'PBP Number'
];

export function exportExcelFile(data?: SummaryData): Promise<Blob> {
    const rowHeight = 60;

    function replaceCommas(input: string): string {
        let i = 0;
        return input.replace(/,/g, (match) => {
          i++;
          return i <= 2 ? '\n' : match;
        });
    }

    function labelRow(label: string, value?: string, boldValue = false): Row {
        return [
            { value: label, fontWeight: 'bold' },
            { value: value ?? '', fontWeight: boldValue ? 'bold' : undefined }
        ];
    }

    function tableHeadingRow(): Row {
        return TABLE_COLUMN_HEADINGS.map((heading, index) => ({
            value: heading,
            fontWeight: 'bold' as const,
            textColor: HEADER_TEXT_COLOR,
            backgroundColor: HEADER_BACKGROUND_COLOR,
            wrap: index === WRAPPED_COLUMN_INDEX
        }));
    }

    function tableTitleRow(title: string): Row {
        const titleCell: Cell = {
            value: title,
            fontSize: 12,
            fontWeight: 'bold',
            align: 'center',
            alignVertical: 'center',
            columnSpan: COLUMN_COUNT
        };
        return [titleCell, ...new Array<null>(COLUMN_COUNT - 1).fill(null)];
    }

    function planRow(item: PartCD): Row {
        const terminationDate = item.TerminationDate.trim() === '- -' ? '' : item.TerminationDate;
        return [
            { value: item.ActivePlan, height: rowHeight },
            { value: item.EnrollmentDate },
            { value: terminationDate },
            { value: replaceCommas(item.NameAddress), wrap: true },
            { value: item.ContractNumber },
            { value: item.PBPNumber }
        ];
    }

    function addPlanTable(sheet: SparseSheet, title: string, parts: Part[], offsetAmendedRows: boolean): void {
        const titleRowNumber = sheet.maxRow + 1;
        sheet.setRow(titleRowNumber, tableTitleRow(title));

        let rowNumber = sheet.addRow(tableHeadingRow()) + 1;

        parts.forEach((item) => {
            sheet.setRow(rowNumber, planRow(item.PaidData));
            rowNumber = rowNumber + 1;

            item.PaidDataHistory.forEach((amendedItem) => {
                // Reproduces the original ExcelJS offset for amended Part D records.
                const target = offsetAmendedRows ? AMENDED_ROW_OFFSET + rowNumber : rowNumber;
                sheet.setRow(target, planRow(amendedItem));
                rowNumber = rowNumber + 1;
            });
        });
    }

    const sheet = createSparseSheet();

    sheet.addRow(labelRow('First Name + Last Name', `${data?.FirstName} ${data?.LastName}`, true));
    sheet.addRow(labelRow('Claim Number', data?.ClaimNumber, true));
    sheet.addRow(labelRow('MBI', data?.MedicareId, true));
    sheet.addRow([]);
    sheet.addRow(labelRow('Part A Effective Date', data?.PartAEffectiveDate));
    sheet.addRow(labelRow('Part A Termination Date', data?.PartATerminationDate));
    sheet.addRow(labelRow('Part B Effective Date', data?.PartBEffectiveDate));
    sheet.addRow(labelRow('Part B Termination Date', data?.PartBTerminationDate));
    sheet.addRow([]);

    if(data != undefined){
        //Part C records
        if(data.PartC != undefined && data.PartC.length > 0)
        {
            addPlanTable(sheet, 'Part C', data.PartC, false);
            sheet.addRow([]);
            sheet.addRow([]);
        }        

        // Part D records
        if(data.PartD != undefined && data.PartD.length > 0)
        {
            addPlanTable(sheet, 'Part D', data.PartD, true);
        }
    }

    return writeXlsxFile(sheet.toSheetData(), { sheet: 'Enrollment Data', columns: COLUMNS }).toBlob();
}